import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { PUBLIC_IMAGES } from '../../constants/publicAssets';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';

function getLoginErrorMessage(error) {
  if (!error) return null;
  if (error.status === 401) return 'Invalid email or password.';
  if (error.status === 400) return error.message;
  if (error.status === 503) return 'The sign-in service is temporarily unavailable. Please try again shortly.';
  if (error.message?.toLowerCase().includes('failed to fetch')) {
    return 'Unable to reach the server. Check your connection and try again.';
  }
  return error.message || 'Unable to sign in right now. Please try again.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, initializing, authError, needsOnboarding } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const sessionExpired = searchParams.get('reason') === 'session-expired';
  const redirectTo = location.state?.from || '/';

  useEffect(() => {
    if (sessionExpired) {
      setError('Your session has expired. Please sign in again.');
    } else if (authError) {
      setError(authError);
    }
  }, [sessionExpired, authError]);

  if (initializing) {
    return <LoadingOverlay loading />;
  }

  if (isAuthenticated) {
    return <Navigate to={needsOnboarding ? '/onboarding' : redirectTo} replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    const trimmedEmail = username.trim();
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }

    setSubmitting(true);
    try {
      await login(trimmedEmail, password);
      navigate(redirectTo, { replace: true });
    } catch (submitError) {
      setError(getLoginErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#EEF1F5] px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-slate-200/90 bg-white p-8 shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src={PUBLIC_IMAGES.logo}
            alt="Venue Intelligence"
            className="mb-4 h-14 w-14 object-contain"
          />
          <h1 className="font-display text-2xl font-bold tracking-[-0.03em] text-[#201C35]">
            Venue Intelligence
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Sign in to access the revenue dashboard
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div>
            <label
              htmlFor="login-username"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
            >
              Email
            </label>
            <input
              id="login-username"
              name="email"
              type="email"
              autoComplete="email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="you@company.com"
              disabled={submitting}
              className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-[0_2px_8px_rgba(15,23,42,0.06)] outline-none transition focus:border-[#201C35] focus:ring-2 focus:ring-[#201C35]/15"
            />
          </div>

          <div>
            <label
              htmlFor="login-password"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                disabled={submitting}
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 pr-11 text-sm text-slate-800 shadow-[0_2px_8px_rgba(15,23,42,0.06)] outline-none transition focus:border-[#201C35] focus:ring-2 focus:ring-[#201C35]/15"
              />
              <button
                type="button"
                onClick={() => setShowPassword((open) => !open)}
                className="absolute inset-y-0 right-0 inline-flex w-11 items-center justify-center text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            className="mt-2 w-full"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
